import { useMemo } from 'react';

import useSWR from 'swr';

import { ChatChannelAccessService } from '@diagram/services/ChatChannelAccessService';

import { DiagramChatChannel } from 'models';
import { ChatHelpers } from 'helpers/ChatHelpers';

import { useCurrentUser } from 'hooks/use-current-user';

/** The users and companies that were given access to the chat channels of a project. */
const useProjectChatChannelAccess = (projectId?: DiagramChatChannel['project'] | null) => {
    const user = useCurrentUser();
    const enabled = !!user && !!projectId;

    const { data: collaborators = [], isLoading: isLoadingCollaborators } = useSWR(
        enabled ? ChatHelpers.swr.channelCollaborators(projectId) : null,
        () => ChatChannelAccessService.listCollaborators(projectId!),
    );

    const { data: companies = [], isLoading: isLoadingCompanies } = useSWR(
        enabled ? ChatHelpers.swr.channelCompanies(projectId) : null,
        () => ChatChannelAccessService.listCompanies(projectId!),
    );

    return {
        collaborators,
        companies,
        isLoading: isLoadingCollaborators || isLoadingCompanies,
    };
};

/** The users and companies that were given access to a single chat channel. */
const useChatChannelAccess = (channel?: Pick<DiagramChatChannel, 'id' | 'project'> | null) => {
    const { collaborators, companies, isLoading } = useProjectChatChannelAccess(channel?.project);

    return useMemo(() => {
        const channelCollaborators = collaborators.filter((row) => row.channel === channel?.id);
        const channelCompanies = companies.filter((row) => row.channel === channel?.id);

        return {
            collaborators: channelCollaborators,
            companies: channelCompanies,
            userIds: channelCollaborators.map((row) => row.user),
            companyIds: ChatHelpers.companyIds(channel?.id, companies),
            // Includes the subcontractors, to tell whether someone's own company is part of the channel
            memberCompanyIds: ChatHelpers.memberCompanyIds(channelCompanies),
            isLoading,
        };
    }, [collaborators, companies, channel?.id, isLoading]);
};

export { useChatChannelAccess, useProjectChatChannelAccess };
